import { motion } from 'framer-motion';
import { useEffect } from 'react';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';
import FloatingButtons from '../home/components/FloatingButtons';
import { useInView } from '../home/hooks/useInView';

const Muret = () => {
  const [heroRef, heroInView] = useInView({ threshold: 0.1 });
  const [servicesRef, servicesInView] = useInView({ threshold: 0.1 });
  const [testimonialsRef, testimonialsInView] = useInView({ threshold: 0.1 });

  useEffect(() => {
    document.title = 'Réparation de fenêtres à Muret – Intervention rapide 24h/24 | Vitrerie 31';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Spécialiste réparation fenêtres à Muret (31600). Intervention sous 2h pour fenêtres PVC, bois, alu. Devis gratuit. ☎ 05 54 54 25 45');
    }
  }, []);

  const services = [
    { icon: 'ri-tools-line', title: 'Réparation Fenêtres PVC', desc: 'Joints, mécanismes, vitrage' },
    { icon: 'ri-hammer-line', title: 'Fenêtres Bois', desc: 'Rénovation et entretien' },
    { icon: 'ri-window-line', title: 'Fenêtres Aluminium', desc: 'Réparation et remplacement' },
    { icon: 'ri-shield-check-line', title: 'Vitrerie d\'urgence', desc: 'Intervention 24h/24' }
  ];

  const testimonials = [
    {
      name: 'Pierre Lacoste',
      location: 'Centre historique Muret',
      text: 'Fenêtres anciennes en bois à rénover. Travail remarquable qui a respecté le cachet de ma maison. Très professionnel !',
      rating: 5
    },
    {
      name: 'Sylvie Moreau',
      location: 'Quartier Estantens',
      text: 'Vitrage cassé suite à un accident. Intervention en urgence le dimanche. Service impeccable et rapide.',
      rating: 5
    },
    {
      name: 'François Bernard',
      location: 'Quartier Ox',
      text: 'Réparation de mécanismes sur fenêtres PVC. Prix honnête et travail soigné. Je recommande vivement !',
      rating: 5
    }
  ];

  const problemesTypiques = [
    { icon: 'ri-ancient-gate-line', title: 'Fenêtres anciennes', desc: 'Rénovation patrimoine bois' },
    { icon: 'ri-water-flash-line', title: 'Humidité Garonne', desc: 'Joints et étanchéité' },
    { icon: 'ri-sun-line', title: 'Exposition sud', desc: 'Dégradation accélérée' },
    { icon: 'ri-lock-unlock-line', title: 'Mécanismes grippés', desc: 'Usure et entretien' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section ref={heroRef} className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-50 via-white to-orange-50"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-transparent to-transparent"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <i className="ri-map-pin-line"></i>
              <span>Muret - Haute-Garonne (31600)</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Réparation de fenêtres à <strong className="text-teal-600">Muret</strong>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Expert en rénovation de fenêtres anciennes et modernes au bord de la Garonne
            </p>

            <div className="flex flex-wrap justify-center gap-4 mb-12">
              <a
                href="tel:0554542545"
                className="inline-flex items-center gap-2 bg-teal-600 text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-teal-700 transition-all shadow-lg hover:scale-105 whitespace-nowrap cursor-pointer active:scale-95"
              >
                <i className="ri-phone-line text-xl"></i>
                05 54 54 25 45
              </a>
              <a
                href="#contact"
                className="inline-flex items-center gap-2 bg-white text-teal-600 px-8 py-4 rounded-full font-semibold text-lg hover:bg-gray-50 transition-all shadow-lg hover:scale-105 whitespace-nowrap cursor-pointer border-2 border-teal-600 active:scale-95"
              >
                <i className="ri-message-line text-xl"></i>
                Devis gratuit
              </a>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-time-line text-2xl text-teal-600"></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Intervention rapide</h3>
                <p className="text-gray-600 text-sm">Sous 2h à Muret</p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-map-pin-2-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">À 20 km de Toulouse</h3>
                <p className="text-gray-600 text-sm">Tous quartiers desservis</p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-24-hours-line text-2xl text-teal-600"></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Urgences 24h/24</h3>
                <p className="text-gray-600 text-sm">7 jours sur 7</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section ref={servicesRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={servicesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Nos services à <strong className="text-teal-600">Muret</strong>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Spécialistes de la rénovation de fenêtres anciennes et modernes
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={servicesInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gradient-to-br from-teal-50 to-orange-50 rounded-lg p-6 hover:shadow-xl transition-all cursor-pointer group"
              >
                <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center mb-4 group-hover:bg-teal-500 transition-colors">
                  <i className={`${service.icon} text-3xl text-teal-600 group-hover:text-white transition-colors`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{service.title}</h3>
                <p className="text-gray-600">{service.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Problèmes Typiques */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Problèmes spécifiques à <strong className="text-teal-600">Muret</strong>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Ville historique avec des contraintes particulières
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {problemesTypiques.map((probleme, index) => (
              <div
                key={index}
                className="bg-white rounded-lg p-6 shadow-lg hover:shadow-xl transition-all"
              >
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                  <i className={`${probleme.icon} text-2xl text-orange-600`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{probleme.title}</h3>
                <p className="text-gray-600 text-sm">{probleme.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-gradient-to-r from-teal-500 to-orange-500 rounded-lg p-8 text-white text-center">
            <h3 className="text-2xl font-bold mb-4">Muret : ville historique au bord de la Garonne</h3>
            <p className="text-lg mb-6 opacity-90 max-w-3xl mx-auto">
              Située à <strong>20 km au sud-ouest de Toulouse</strong>, Muret est une ville chargée d'histoire avec un <strong>centre historique</strong> riche en bâtiments anciens. La proximité de la <strong>Garonne</strong> crée une humidité ambiante qui affecte particulièrement les fenêtres. Les quartiers <strong>Estantens, Ox et le Centre-ville</strong> nécessitent une expertise spécifique pour la rénovation de fenêtres anciennes en bois.
            </p>
          </div>
        </div>
      </section>

      {/* Témoignages Clients */}
      <section ref={testimonialsRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={testimonialsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Avis de nos clients à <strong className="text-teal-600">Muret</strong>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Ils nous ont fait confiance pour leurs fenêtres
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={testimonialsInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gradient-to-br from-teal-50 to-orange-50 rounded-lg p-6 shadow-lg"
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-500 text-xl"></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">"{testimonial.text}"</p>
                <div className="border-t border-gray-200 pt-4">
                  <p className="font-bold text-gray-900">{testimonial.name}</p>
                  <p className="text-sm text-gray-600 flex items-center gap-1">
                    <i className="ri-map-pin-line"></i>
                    {testimonial.location}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* NOUVEAU BLOC 1 - Fenêtres Anciennes Patrimoine */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="relative"
        >
          <div className="relative w-full h-[500px] rounded-2xl overflow-hidden shadow-2xl">
            <img
              src="https://public.readdy.ai/ai/img_res/6b57dfd952f15bf84cc546592e6e136c.jpg"
              alt="Restauration fenêtres anciennes patrimoine Muret"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-teal-900/60 via-transparent to-transparent"></div>
            
            <div className="absolute bottom-6 left-6 bg-white rounded-xl shadow-xl p-4">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                  <i className="ri-building-line text-amber-600 text-2xl"></i>
                </div>
                <div>
                  <p className="text-lg font-bold text-gray-900">Patrimoine</p>
                  <p className="text-sm text-gray-600">Expert restauration</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <h3 className="text-3xl font-bold text-gray-900 mb-6">
            <strong className="text-teal-600">Spécialiste Fenêtres Anciennes</strong> à Muret
          </h3>
          <p className="text-gray-600 mb-4 leading-relaxed">
            Muret possède un riche <strong>patrimoine architectural historique</strong> avec de nombreuses bâtisses en briques roses typiques de la région toulousaine. Notre équipe est experte en <strong>restauration de fenêtres anciennes à Muret</strong>, préservant l'authenticité de votre patrimoine tout en améliorant ses performances thermiques et acoustiques.
          </p>
          <p className="text-gray-600 mb-6 leading-relaxed">
            Nous intervenons pour la <strong>rénovation de fenêtres en bois d'époque</strong>, le <strong>remplacement de vitrage simple par double vitrage sur mesure</strong>, la <strong>restauration de menuiseries traditionnelles</strong> et la <strong>réparation de volets anciens</strong>. Notre savoir-faire artisanal respecte les techniques traditionnelles tout en intégrant les technologies modernes d'isolation à Muret.
          </p>

          <div className="space-y-3 mb-6">
            <div className="flex items-start">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                <i className="ri-hammer-line text-2xl text-teal-600"></i>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Restauration Bois Traditionnel</h4>
                <p className="text-gray-600 text-sm">Traitement, ponçage, réparation et finition des menuiseries anciennes en bois massif</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                <i className="ri-window-2-line text-2xl text-orange-600"></i>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Double Vitrage Patrimoine</h4>
                <p className="text-gray-600 text-sm">Installation de double vitrage fin adapté aux châssis anciens sans modification</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                <i className="ri-paint-brush-line text-2xl text-teal-600"></i>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Finitions Authentiques</h4>
                <p className="text-gray-600 text-sm">Peintures et lasures traditionnelles respectant les couleurs d'origine</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                <i className="ri-government-line text-2xl text-orange-600"></i>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Conformité ABF</h4>
                <p className="text-gray-600 text-sm">Respect des prescriptions des Architectes des Bâtiments de France</p>
              </div>
            </div>
          </div>

          <a
            href="#contact"
            className="inline-block bg-gradient-to-r from-teal-500 to-teal-600 text-white px-8 py-4 rounded-full font-semibold hover:from-teal-600 hover:to-teal-700 transition-all shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-phone-line mr-2"></i>
            Devis Restauration Patrimoine
          </a>
        </motion.div>
      </div>

      {/* NOUVEAU BLOC 2 - Problèmes Humidité Garonne */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="order-2 lg:order-1"
        >
          <h3 className="text-3xl font-bold text-gray-900 mb-6">
            <strong className="text-orange-600">Traitement Anti-Humidité</strong> pour Fenêtres à Muret
          </h3>
          <p className="text-gray-600 mb-4 leading-relaxed">
            Située en bordure de la <strong>Garonne</strong>, Muret connaît des problèmes d'<strong>humidité et de condensation sur les fenêtres</strong>. Notre expertise en <strong>traitement anti-humidité des fenêtres à Muret</strong> vous permet de résoudre définitivement ces désagréments : moisissures, condensation, joints noircis et dégradation des menuiseries.
          </p>
          <p className="text-gray-600 mb-6 leading-relaxed">
            Nous proposons des solutions complètes : <strong>remplacement de joints d'étanchéité</strong>, <strong>installation de double vitrage avec gaz argon anti-condensation</strong>, <strong>pose de grilles d'aération</strong> et <strong>traitement hydrofuge des menuiseries bois</strong>. Nos interventions garantissent une ventilation optimale et l'élimination de la condensation à Muret.
          </p>

          <div className="bg-gradient-to-r from-blue-50 to-teal-50 rounded-xl p-6 mb-6">
            <h4 className="font-bold text-gray-900 mb-4 flex items-center">
              <i className="ri-drop-line text-blue-600 mr-2 text-xl"></i>
              Problèmes d'Humidité Résolus
            </h4>
            <div className="space-y-3">
              <div className="flex items-center text-gray-700">
                <i className="ri-close-circle-line text-red-500 mr-3"></i>
                <span className="text-sm line-through text-gray-500">Condensation sur vitres</span>
                <i className="ri-arrow-right-line mx-2 text-teal-600"></i>
                <i className="ri-check-line text-teal-600"></i>
              </div>
              <div className="flex items-center text-gray-700">
                <i className="ri-close-circle-line text-red-500 mr-3"></i>
                <span className="text-sm line-through text-gray-500">Moisissures joints</span>
                <i className="ri-arrow-right-line mx-2 text-teal-600"></i>
                <i className="ri-check-line text-teal-600"></i>
              </div>
              <div className="flex items-center text-gray-700">
                <i className="ri-close-circle-line text-red-500 mr-3"></i>
                <span className="text-sm line-through text-gray-500">Bois gonflé/pourri</span>
                <i className="ri-arrow-right-line mx-2 text-teal-600"></i>
                <i className="ri-check-line text-teal-600"></i>
              </div>
            </div>
          </div>

          <div className="space-y-3 mb-6">
            <div className="flex items-start">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                <i className="ri-contrast-drop-2-line text-2xl text-blue-600"></i>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Double Vitrage Anti-Condensation</h4>
                <p className="text-gray-600 text-sm">Vitrage à isolation renforcée avec intercalaire warm edge évitant les ponts thermiques</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                <i className="ri-wind-line text-2xl text-teal-600"></i>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Ventilation Intégrée</h4>
                <p className="text-gray-600 text-sm">Grilles d'aération hygroréglables pour renouvellement d'air optimal</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                <i className="ri-shield-line text-2xl text-orange-600"></i>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Traitement Hydrofuge Bois</h4>
                <p className="text-gray-600 text-sm">Protection longue durée contre l'humidité et les champignons</p>
              </div>
            </div>
          </div>

          <a
            href="#contact"
            className="inline-block bg-gradient-to-r from-orange-500 to-orange-600 text-white px-8 py-4 rounded-full font-semibold hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-phone-line mr-2"></i>
            Diagnostic Humidité Gratuit
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="relative order-1 lg:order-2"
        >
          <div className="relative w-full h-[500px] rounded-2xl overflow-hidden shadow-2xl">
            <img
              src="https://readdy.ai/api/search-image?query=window%20moisture%20condensation%20problem%20showing%20water%20droplets%20on%20glass%20mold%20on%20frame%20before%20and%20after%20treatment%20comparison%20anti%20humidity%20solution%20professional%20repair%20work%20in%20home%20near%20garonne%20river%20muret%20area%20demonstrating%20dry%20healthy%20window%20simple%20clean%20background&width=800&height=600&seq=muret-humidity-treatment-v1&orientation=landscape"
              alt="Traitement anti-humidité fenêtres Muret Garonne"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-blue-900/60 via-transparent to-transparent"></div>
            
            <div className="absolute bottom-6 right-6 bg-white rounded-xl shadow-xl p-4">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                  <i className="ri-drop-line text-blue-600 text-2xl"></i>
                </div>
                <div>
                  <p className="text-lg font-bold text-gray-900">Anti-Humidité</p>
                  <p className="text-sm text-gray-600">Solution durable</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Quartiers desservis */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Tous les quartiers de <strong className="text-teal-600">Muret</strong>
            </h2>
            <p className="text-lg text-gray-600">Intervention rapide dans toute la commune</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {['Centre historique', 'Estantens', 'Ox', 'Vernet', 'Marclan', 'Oustalous', 'Pyrénées', 'Terrasse'].map((quartier, index) => (
              <div
                key={index}
                className="bg-white rounded-lg p-4 shadow-md hover:shadow-lg transition-all text-center"
              >
                <i className="ri-map-pin-2-fill text-2xl text-teal-600 mb-2"></i>
                <p className="font-semibold text-gray-900">{quartier}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-teal-500 to-orange-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Besoin d'une réparation de fenêtre à Muret ?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Intervention rapide sous 2h - Devis gratuit - Urgences 24h/24
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="tel:0554542545"
              className="inline-flex items-center gap-2 bg-white text-teal-600 px-8 py-4 rounded-full font-semibold text-lg hover:bg-gray-50 transition-all shadow-lg hover:scale-105 whitespace-nowrap cursor-pointer active:scale-95"
            >
              <i className="ri-phone-line text-xl"></i>
              Appelez maintenant
            </a>
            <a
              href="/#contact"
              className="inline-flex items-center gap-2 bg-transparent border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white/10 transition-all shadow-lg hover:scale-105 whitespace-nowrap cursor-pointer active:scale-95"
            >
              <i className="ri-message-line text-xl"></i>
              Demander un devis
            </a>
          </div>
        </div>
      </section>

      {/* Liens vers autres villes */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Nous intervenons aussi dans les villes voisines
          </h3>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="/" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Toulouse</a>
            <span className="text-gray-400">•</span>
            <a href="/colomiers" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Colomiers</a>
            <span className="text-gray-400">•</span>
            <a href="/blagnac" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Blagnac</a>
            <span className="text-gray-400">•</span>
            <a href="/tournefeuille" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Tournefeuille</a>
            <span className="text-gray-400">•</span>
            <a href="/balma" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Balma</a>
            <span className="text-gray-400">•</span>
            <a href="/ramonville" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Ramonville</a>
            <span className="text-gray-400">•</span>
            <a href="/cugnaux" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Cugnaux</a>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default Muret;
